import React from 'react';
import { FD121 } from '../../../services/d202Api';
import { fmt } from '../helpers';

interface DetailTableProps {
  details: Partial<FD121>[];
  selectedRow: number;
  names: Record<string, string>;
  isEditing: boolean;
  setSelectedRow: (idx: number) => void;
  getDetailField: (key: keyof FD121) => FD121[keyof FD121];
  setDetailField: (key: keyof FD121, value: FD121[keyof FD121]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  addDetailRow: () => void;
  deleteDetailRow: () => void;
}

const D202DetailTable: React.FC<DetailTableProps> = ({
  details, selectedRow, names, isEditing,
  setSelectedRow, getDetailField, setDetailField,
  resolveSingleName, addDetailRow, deleteDetailRow,
}) => {
  const selDetail = details[selectedRow] as Partial<FD121> | undefined;
  // Alias for the static frontend-mirror Tool1 shape
  // (onChange={() => setField('fd121_19', LITERAL)}). Writes the selected
  // detail row — same as setDetailField.
  const setField = setDetailField;

  return (
    <div>
      {/* Toolbar */}
      <div className="table-wrapper">
        <div className="table-toolbar">
          <div className="left">
            <button className="btn btn-sm" disabled={!isEditing} onClick={addDetailRow}>明細新增</button>
            <button className="btn btn-sm btn-danger" disabled={!isEditing || details.length === 0} onClick={deleteDetailRow}>明細刪除</button>
          </div>
          <div className="right">
            <button className="btn btn-sm" disabled={!selDetail}>顏色明細</button>
            <button className="btn btn-sm" disabled={!selDetail}>查詢板號/缸號</button>
          </div>
        </div>

        {/* Grid: FD121 product detail */}
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 20 }} data-contract-field="fd121_02">項次</th>
                <th style={{ width: 55 }} data-contract-field="fd121_29">S/C NO</th>
                <th style={{ width: 32 }} data-contract-field="fd121_30">S/C SEQ</th>
                <th style={{ width: 30 }} data-contract-field="fd121_13">P/O 項次</th>
                <th style={{ width: 164 }} data-contract-field="fd121_11">產品代號</th>
                <th style={{ width: 34 }} data-contract-field="fd121_14">等級</th>
                <th className="num" style={{ width: 70 }} data-contract-field="fd121_15">繳庫數量</th>
                <th className="num" style={{ width: 69 }} data-contract-field="fd121_17">繳庫單價</th>
                <th className="num" style={{ width: 82 }} data-contract-field="fd121_41">包缸費</th>
                <th className="num" style={{ width: 88 }} data-contract-field="fd121_18">繳庫金額</th>
                <th style={{ width: 26 }} data-contract-field="fd121_19">ASSORT.</th>
              </tr>
            </thead>
            <tbody>
              {details.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', color: '#999' }}>無明細資料</td>
                </tr>
              ) : details.map((d, i) => (
                <tr
                  key={i}
                  className={i === selectedRow ? 'selected' : ''}
                  onClick={() => setSelectedRow(i)}
                >
                  <td>{d.fd121_02 || i + 1}</td>
                  <td>{d.fd121_29 || ''}</td>
                  <td>{d.fd121_30 || ''}</td>
                  <td>{d.fd121_13 || ''}</td>
                  <td>{d.fd121_11 || d.fd121_10 || ''}</td>
                  <td>{d.fd121_14 || ''}</td>
                  <td className="num">
                    {i === selectedRow && isEditing ? (
                      <input
                        type="number"
                        value={d.fd121_15 ?? ''}
                        onChange={e => setDetailField('fd121_15', parseFloat(e.target.value) || 0)}
                        style={{ width: '100%', textAlign: 'right' }} data-contract-field="fd121_15"
                      />
                    ) : (
                      fmt(d.fd121_15)
                    )}
                  </td>
                  <td className="num">
                    {i === selectedRow && isEditing ? (
                      <input
                        type="number"
                        value={d.fd121_17 ?? ''}
                        onChange={e => setDetailField('fd121_17', parseFloat(e.target.value) || 0)}
                        style={{ width: '100%', textAlign: 'right' }} data-contract-field="fd121_17"
                      />
                    ) : (
                      fmt(d.fd121_17)
                    )}
                  </td>
                  <td className="num">{fmt(d.fd121_41)}</td>
                  <td className="num">{fmt(d.fd121_18)}</td>
                  <td>{d.fd121_19 || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Panel below Grid */}
      <div className="detail-panels">
          {/* Panel 1: 產品規格 */}
          <div className="detail-panel">
            <div className="panel-title">產品資訊</div>
            <div className="form-row">
              <label>產品名稱</label>
              <textarea
                value={String(selDetail?.fd121_12 || '--')}
                onChange={e => setDetailField('fd121_12', e.target.value)}
                disabled={!isEditing || !selDetail}
                rows={2} data-contract-field="fd121_12"
              />
            </div>
            <div className="form-row">
              <label>產品規格</label>
              <textarea
                value={String(selDetail?.fd121_31 || '--')}
                onChange={e => setDetailField('fd121_31', e.target.value)}
                disabled={!isEditing || !selDetail}
                rows={2} data-contract-field="fd121_31"
              />
            </div>
            <div className="form-row">
              <label>加工別</label>
              <textarea
                value={String(selDetail?.fd121_32 || '--')}
                onChange={e => setDetailField('fd121_32', e.target.value)}
                disabled={!isEditing || !selDetail}
                rows={2} data-contract-field="fd121_32"
              />
            </div>
            {/* ASSORT Y/N — domain mirrors PWD202F1-VAL-FD121_19-Y-OR-N
                code_labels (contract: Y=ASSORT 有顏色, N=ASSORT 無顏色).
                Native radio + setField shape is required by
                check-frontend-contract-mirror.js Tool1. */}
            <div className="form-row">
              <label>ASSORT</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name={`fd121_19_assort_${selectedRow}`}
                    value="Y"
                    disabled={!isEditing || !selDetail}
                    checked={selDetail?.fd121_19 === 'Y'}
                    onChange={() => setField('fd121_19', 'Y')} data-contract-field="fd121_19"
                  />
                  ASSORT 有顏色
                </label>
                <label>
                  <input
                    type="radio"
                    name={`fd121_19_assort_${selectedRow}`}
                    value="N"
                    disabled={!isEditing || !selDetail}
                    checked={selDetail?.fd121_19 === 'N'}
                    onChange={() => setField('fd121_19', 'N')} data-contract-field="fd121_19"
                  />
                  ASSORT 無顏色
                </label>
              </div>
            </div>
          </div>

          {/* Panel 2: 單位/換算 */}
          <div className="detail-panel">
            <div className="panel-title">單位/幅寬</div>
            <div className="form-row">
              <label>繳庫單位</label>
              <input
                type="text"
                value={String(selDetail?.fd121_16 || '--')}
                onChange={e => setDetailField('fd121_16', e.target.value)}
                disabled={!isEditing || !selDetail}
                maxLength={5} data-contract-field="fd121_16"
              />
            </div>
            <div className="form-row">
              <label>庫存單位</label>
              <input type="text" value={String(selDetail?.fd121_28 || '--')} disabled readOnly data-contract-field="fd121_28" />
            </div>
            <div className="form-row">
              <label>單位換算率</label>
              <input
                type="number"
                value={selDetail?.fd121_20 ?? ''}
                onChange={e => setDetailField('fd121_20', parseFloat(e.target.value) || 0)}
                disabled={!isEditing || !selDetail}
                style={{ textAlign: 'right' }} data-contract-field="fd121_20"
              />
            </div>
            <div className="form-row">
              <label>累計繳庫量</label>
              <input type="text" value={selDetail ? fmt(selDetail.fd121_38) : '--'} disabled readOnly style={{ textAlign: 'right' }} data-contract-field="fd121_38" />
            </div>
            <div className="form-row">
              <label>幅寬1</label>
              <input
                type="text"
                value={String(selDetail?.fd121_24 || '--')}
                onChange={e => setDetailField('fd121_24', e.target.value)}
                disabled={!isEditing || !selDetail}
                maxLength={20} data-contract-field="fd121_24"
              />
            </div>
            <div className="form-row">
              <label>幅寬2</label>
              <input
                type="text"
                value={String(selDetail?.fd121_25 || '--')}
                onChange={e => setDetailField('fd121_25', e.target.value)}
                disabled={!isEditing || !selDetail}
                maxLength={20} data-contract-field="fd121_25"
              />
            </div>
          </div>

          {/* Panel 3: 物性 */}
          <div className="detail-panel">
            <div className="panel-title">物性資料</div>
            <div className="form-row">
              <label>幅寬3</label>
              <input
                type="text"
                value={String(selDetail?.fd121_26 || '--')}
                onChange={e => setDetailField('fd121_26', e.target.value)}
                disabled={!isEditing || !selDetail}
                maxLength={20} data-contract-field="fd121_26"
              />
            </div>
            <div className="form-row">
              <label>幅寬4</label>
              <input
                type="text"
                value={String(selDetail?.fd121_27 || '--')}
                onChange={e => setDetailField('fd121_27', e.target.value)}
                disabled={!isEditing || !selDetail}
                maxLength={20} data-contract-field="fd121_27"
              />
            </div>
            <div className="form-row">
              <label>針孔(內)</label>
              <input
                type="number"
                value={selDetail?.fd121_33 ?? ''}
                onChange={e => setDetailField('fd121_33', parseFloat(e.target.value) || 0)}
                disabled={!isEditing || !selDetail}
                style={{ textAlign: 'right' }} data-contract-field="fd121_33"
              />
            </div>
            <div className="form-row">
              <label>針孔(外)</label>
              <input
                type="number"
                value={selDetail?.fd121_34 ?? ''}
                onChange={e => setDetailField('fd121_34', parseFloat(e.target.value) || 0)}
                disabled={!isEditing || !selDetail}
                style={{ textAlign: 'right' }} data-contract-field="fd121_34"
              />
            </div>
            <div className="form-row">
              <label>碼重</label>
              <input
                type="number"
                value={selDetail?.fd121_35 ?? ''}
                onChange={e => setDetailField('fd121_35', parseFloat(e.target.value) || 0)}
                disabled={!isEditing || !selDetail}
                style={{ textAlign: 'right' }} data-contract-field="fd121_35"
              />
            </div>
            <div className="form-row">
              <label>米平方重</label>
              <input
                type="number"
                value={selDetail?.fd121_36 ?? ''}
                onChange={e => setDetailField('fd121_36', parseFloat(e.target.value) || 0)}
                disabled={!isEditing || !selDetail}
                style={{ textAlign: 'right' }} data-contract-field="fd121_36"
              />
            </div>
          </div>
        </div>

      {/* Total items display */}
      <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>
        明細共 {details.length} 項
      </div>
    </div>
  );
};

export default React.memo(D202DetailTable);
